/**
 * Authorization utility functions
 * Checks all user roles to determine access permissions
 */

// Define authorized roles
const AUTHORIZED_ROLES = ['PlantHead', 'BusinessUser'];

/**
 * Get authorized roles from all user roles
 * Checks the entire allRoles array to find which roles are authorized
 * @param {string[]} allRoles - Array of all user roles
 * @returns {string[]} Array of authorized roles the user has
 */
export const getAuthorizedRoles = (allRoles: string[]): string[] => {
  if (!allRoles || allRoles.length === 0) {
    return [];
  }
  
  return allRoles.filter(role => AUTHORIZED_ROLES.includes(role));
};

/**
 * Check if user has an authorized role
 * Iterates through all user roles to find at least one authorized role
 * @param {string[]} allRoles - Array of all user roles
 * @returns {boolean} True if user has at least one authorized role
 */
export const isAuthorizedUser = (allRoles: string[]): boolean => {
  if (!allRoles || allRoles.length === 0) {
    return false;
  }
  
  // Check all roles to find if any is authorized
  return allRoles.some(role => AUTHORIZED_ROLES.includes(role));
};

/**
 * Get authorization status
 * Checks all user roles and returns complete authorization information
 * @param {string[]} allRoles - Array of all user roles
 * @returns {{ authorizeduser: boolean, hasRole: boolean, authorizedRoles: string[] }} Authorization status
 */
export const getAuthorizationStatus = (allRoles: string[]): { authorizeduser: boolean; hasRole: boolean; authorizedRoles: string[] } => {
  const hasRole = allRoles && allRoles.length > 0;
  const authorizedRoles = getAuthorizedRoles(allRoles);
  const authorizeduser = authorizedRoles.length > 0;
  
  if (import.meta.env.DEV) {
    console.log('User All Roles:', allRoles);
    console.log('Authorized Roles Found:', authorizedRoles);
    console.log('Authorization Status:', { authorizeduser, hasRole });
  }
  
  return {
    authorizeduser,
    hasRole,
    authorizedRoles
  };
};
